/*
 * David Mallett — Cart Progress Bar
 *
 * Architecture:
 *   1. Server renders only an empty <dm-cart-progress> shell with data-country/currency/lang/cart-total
 *   2. JS does a FULL render on connect (builds the markup) and TARGETED updates on cart-total change
 *      (just fill width + message + milestone classes — so CSS transitions on width work)
 *   3. line-item:will-change → optimistic update from cached cart (instant feedback on click)
 *   4. cart:change → authoritative update from server response (corrects prediction)
 *   5. The 1st valid country we see is remembered as "trustedCountry" and used to override
 *      any section refresh that brings the wrong country (cached / different market context)
 */
(function () {
  if (window.customElements.get('dm-cart-progress')) return;

  // ============= Config =============
  var EU = ['FR','BE','DE','LU','NL','AT','IT','PT','ES','IE','GB','HU','LV','LT','PL','SK','SI','SE','CH','CZ','DK','EE','BG','HR','FI','IS','NO','TR'];

  var SHIP_T = { EUR: 15000, GBP: 13000, CHF: 14000, USD: 16500, SEK: 170000, NOK: 175000, DKK: 110000, PLN: 65000, CZK: 380000 };
  var GIFT_T = { EUR: 10000, USD: 10000, GBP: 9000, CHF: 9500, SEK: 115000, NOK: 115000, DKK: 75000, PLN: 45000, CZK: 250000 };

  var BOTTLE = '<svg class="dm-cp__icon dm-cp__icon--base" viewBox="0 0 14 14" aria-hidden="true" focusable="false"><path d="M5.6 1.7h2.8v1.5H5.6V1.7Zm-.5 2h3.8l.6 1.3H4.5l.6-1.3Zm-.5 1.7h4.8a1 1 0 0 1 1 1v5.2a1 1 0 0 1-1 1H4.6a1 1 0 0 1-1-1V6.4a1 1 0 0 1 1-1Z" fill="none" stroke="currentColor" stroke-width=".9" stroke-linejoin="round"/></svg>';
  var TRUCK = '<svg class="dm-cp__icon dm-cp__icon--base" viewBox="0 0 16 14" aria-hidden="true" focusable="false"><path d="M1.4 3.4h6.8v6H1.4v-6Zm6.8 2h2.4l1.9 1.9v2.1H8.2V5.4ZM4.1 11.5a1 1 0 1 0 0-2 1 1 0 0 0 0 2Zm7.2 0a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z" fill="none" stroke="currentColor" stroke-width=".9" stroke-linejoin="round"/></svg>';
  var CHECK = '<svg class="dm-cp__icon dm-cp__icon--check" viewBox="0 0 12 12" aria-hidden="true" focusable="false"><path d="M2.7 6.3 4.9 8.4 9.5 3.6" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  // ============= Module state =============
  var trustedCountry = null;   // First valid country we ever see (assumed correct for buyer's market)
  var trustedCurrency = null;
  var cartCache = null;         // Last known cart (for optimistic predictions)

  // ============= Helpers =============
  function formatMoney(cents, currency, lang) {
    var amount = Math.round(cents / 100);
    var locale = lang === 'en' ? 'en-US' : 'fr-FR';
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency', currency: currency,
        minimumFractionDigits: 0, maximumFractionDigits: 0
      }).format(amount);
    } catch (e) {
      return amount + ' ' + currency;
    }
  }

  function computeState(country, currency, totalCents) {
    var hasShipping = EU.indexOf(country) !== -1 || country === 'US';
    var shipT = 0;
    if (country === 'US') shipT = 45000;
    else if (hasShipping) shipT = SHIP_T[currency] || 15000;
    var giftT = GIFT_T[currency] || 10000;
    var maxT = hasShipping && shipT > giftT ? shipT : giftT;
    if (maxT <= 0) maxT = giftT;
    return {
      currency: currency,
      hasShipping: hasShipping,
      shipT: shipT,
      giftT: giftT,
      pct: Math.min(100, Math.floor(totalCents * 100 / maxT)),
      giftPos: Math.floor(giftT * 100 / maxT),
      giftReached: totalCents >= giftT,
      shipReached: hasShipping && totalCents >= shipT,
      remGift: Math.max(0, giftT - totalCents),
      remShip: Math.max(0, shipT - totalCents)
    };
  }

  function buildMessage(state, lang) {
    if (state.shipReached) {
      return lang === 'en'
        ? 'Enjoy your complimentary <em>Shampoo Pure 50ML</em> &amp; <em>shipping</em>'
        : 'Profitez de votre <em>Shampoo Pure 50ML</em> et de votre <em>livraison</em> offerts';
    }
    if (state.giftReached && state.hasShipping) {
      var rs = formatMoney(state.remShip, state.currency, lang);
      return lang === 'en'
        ? 'Your <em>Shampoo Pure 50ML</em> is unlocked. <strong>' + rs + '</strong> away from free shipping'
        : 'Votre <em>Shampoo Pure 50ML</em> est offert. Plus que <strong>' + rs + '</strong> pour la livraison offerte';
    }
    if (state.giftReached) {
      return lang === 'en'
        ? 'Enjoy your complimentary <em>Shampoo Pure 50ML</em>'
        : 'Profitez de votre <em>Shampoo Pure 50ML</em> offert';
    }
    var rg = formatMoney(state.remGift, state.currency, lang);
    return lang === 'en'
      ? '<strong>' + rg + '</strong> away from a complimentary <em>Shampoo Pure 50ML</em>'
      : 'Plus que <strong>' + rg + '</strong> pour recevoir votre <em>Shampoo Pure 50ML</em> offert';
  }

  function readAttrs(host) {
    // Use trusted values over per-element attributes (defends against stale section refreshes)
    var country = (trustedCountry || host.dataset.country || 'XX').toUpperCase();
    var currency = (trustedCurrency || host.dataset.currency || 'EUR').toUpperCase();
    var lang = (host.dataset.lang || 'fr').toLowerCase();
    var total = parseInt(host.dataset.cartTotal || '0', 10);
    if (isNaN(total) || total < 0) total = 0;
    return { country: country, currency: currency, lang: lang, total: total };
  }

  // ============= Render: full build (initial mount or country/currency change) =============
  function fullRender(host) {
    var a = readAttrs(host);
    var state = computeState(a.country, a.currency, a.total);
    var msg = buildMessage(state, a.lang);

    var html = '<div class="dm-cp">';
    html += '<p class="dm-cp__message">' + msg + '</p>';
    html += '<div class="dm-cp__track" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="' + state.pct + '">';
    html += '<span class="dm-cp__fill" style="width:' + state.pct + '%"></span>';
    html += '<span class="dm-cp__milestone dm-cp__milestone--shampoo' + (state.giftReached ? ' is-reached' : '') + '" style="--dm-cp-pos:' + state.giftPos + '%" aria-label="Shampoo Pure">' + BOTTLE + CHECK + '</span>';
    if (state.hasShipping) {
      html += '<span class="dm-cp__milestone dm-cp__milestone--shipping' + (state.shipReached ? ' is-reached' : '') + '" style="--dm-cp-pos:100%" aria-label="Livraison offerte">' + TRUCK + CHECK + '</span>';
    }
    html += '</div></div>';

    host.innerHTML = html;
    host.setAttribute('data-ready', '');
  }

  // ============= Render: targeted update (cart-total change) =============
  // Keeps DOM nodes intact so CSS transitions on fill width fire.
  function smoothUpdate(host) {
    var a = readAttrs(host);
    var state = computeState(a.country, a.currency, a.total);
    var msg = buildMessage(state, a.lang);

    // If the structural shape changed (1 vs 2 milestones), do a full rebuild.
    var hasShippingInDOM = !!host.querySelector('.dm-cp__milestone--shipping');
    if (state.hasShipping !== hasShippingInDOM) {
      fullRender(host);
      return;
    }

    var fill = host.querySelector('.dm-cp__fill');
    if (fill) fill.style.width = state.pct + '%';

    var track = host.querySelector('.dm-cp__track');
    if (track) track.setAttribute('aria-valuenow', state.pct);

    var message = host.querySelector('.dm-cp__message');
    if (message) message.innerHTML = msg;

    var shampooMS = host.querySelector('.dm-cp__milestone--shampoo');
    if (shampooMS) shampooMS.classList.toggle('is-reached', state.giftReached);

    var shippingMS = host.querySelector('.dm-cp__milestone--shipping');
    if (shippingMS) shippingMS.classList.toggle('is-reached', state.shipReached);
  }

  // ============= Custom element =============
  class DmCartProgress extends HTMLElement {
    static get observedAttributes() {
      return ['data-cart-total', 'data-country', 'data-currency', 'data-lang'];
    }
    connectedCallback() {
      // Capture the first non-empty country / currency we see as our source of truth
      if (!trustedCountry && this.dataset.country) {
        trustedCountry = this.dataset.country.toUpperCase();
      }
      if (!trustedCurrency && this.dataset.currency) {
        trustedCurrency = this.dataset.currency.toUpperCase();
      }
      fullRender(this);
    }
    attributeChangedCallback(name, oldValue, newValue) {
      if (!this.isConnected) return;
      if (oldValue === newValue) return;
      if (name === 'data-cart-total') {
        smoothUpdate(this);
      } else {
        fullRender(this);
      }
    }
  }

  window.customElements.define('dm-cart-progress', DmCartProgress);

  // ============= Helpers to apply state to all bars =============
  function applyTotal(totalCents, currency) {
    var bars = document.querySelectorAll('dm-cart-progress');
    for (var i = 0; i < bars.length; i++) {
      if (currency) bars[i].setAttribute('data-currency', currency.toUpperCase());
      bars[i].setAttribute('data-cart-total', String(totalCents));
    }
  }

  function computeShippableTotal(cart) {
    if (!cart || !cart.items) return 0;
    var sub = 0;
    for (var i = 0; i < cart.items.length; i++) {
      var it = cart.items[i];
      if (it.requires_shipping) sub += (it.final_line_price || it.line_price || 0);
    }
    var disc = 0;
    var apps = cart.cart_level_discount_applications || [];
    for (var j = 0; j < apps.length; j++) {
      disc += (apps[j].total_allocated_amount || 0);
    }
    return Math.max(0, sub - disc);
  }

  // ============= Optimistic update on click (instant feedback) =============
  document.addEventListener('line-item:will-change', function (event) {
    if (!cartCache || !event.detail) return;
    var targetQty = event.detail.targetQuantity;
    if (typeof targetQty !== 'number') return;

    var lineItem = event.target && event.target.closest && event.target.closest('line-item');
    if (!lineItem) return;
    var keyEl = lineItem.querySelector('[data-line-key]');
    var lineKey = keyEl && keyEl.getAttribute('data-line-key');
    if (!lineKey) return;

    var item = null;
    for (var i = 0; i < cartCache.items.length; i++) {
      if (cartCache.items[i].key === lineKey) { item = cartCache.items[i]; break; }
    }
    if (!item) return;

    // Predict the new shippable total
    var predicted = 0;
    for (var k = 0; k < cartCache.items.length; k++) {
      var it = cartCache.items[k];
      if (!it.requires_shipping) continue;
      var qty = (it.key === lineKey) ? targetQty : it.quantity;
      predicted += (it.final_price || it.price || 0) * qty;
    }
    var disc = 0;
    var apps = cartCache.cart_level_discount_applications || [];
    for (var d = 0; d < apps.length; d++) disc += (apps[d].total_allocated_amount || 0);
    var newTotal = Math.max(0, predicted - disc);

    applyTotal(newTotal, cartCache.currency);
  });

  // ============= Authoritative update from server response =============
  document.addEventListener('cart:change', function (event) {
    var cart = event.detail && event.detail.cart;
    if (!cart) return;
    cartCache = cart;
    if (cart.currency) trustedCurrency = cart.currency.toUpperCase();
    applyTotal(computeShippableTotal(cart), cart.currency);
  });

  // ============= Initial cart cache (for optimistic updates on first click) =============
  var rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  fetch(rootUrl + 'cart.js', { credentials: 'same-origin' })
    .then(function (r) { return r.ok ? r.json() : null; })
    .then(function (cart) { if (cart) cartCache = cart; })
    .catch(function () {});
})();
